import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { UserPlus, X } from 'lucide-react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { CopyableLink } from '@/components/CopyableLink'
import { api } from '@/lib/api'
import { setPrimaryColor, applyPrimaryColor } from '@/lib/auth'
import { usePermissions } from '@/hooks/usePermissions'
import { useRoles } from '@/hooks/useRoles'
import { RolesTab } from './RolesTab'
import { createInviteSchema, type CreateInviteFormData } from '@/validators/invite'
import {
  useTeam,
  useUpdateUserRole,
  useDeactivateUser,
  useReactivateUser,
  useGeneratePasswordReset,
  type TeamMember,
  type UserRole,
} from '@/hooks/useTeam'
import { useAuditLogs, type AuditAction } from '@/hooks/useAuditLogs'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Invite {
  id: string
  email: string
  role: UserRole
  tenantRoleId: string | null
  tenantRole: { id: string; name: string } | null
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED'
  expiresAt: string
  createdAt: string
}

interface CreatedInvite extends Invite {
  token: string
}

interface UserMe {
  id: string
  name: string
  email: string
  role: string
  tenant: { id: string; name: string; primaryColor: string }
}

// ─── Schemas ──────────────────────────────────────────────────────────────────

const profileSchema = z
  .object({
    name: z.string().min(2, 'Nome deve ter ao menos 2 caracteres').optional().or(z.literal('')),
    currentPassword: z.string().optional().or(z.literal('')),
    newPassword: z.string().min(6, 'Mínimo 6 caracteres').optional().or(z.literal('')),
  })
  .refine(
    (d) => !(d.newPassword && !d.currentPassword),
    { message: 'Informe a senha atual', path: ['currentPassword'] },
  )

type ProfileFormData = z.infer<typeof profileSchema>

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  OPERATOR: 'Operador',
  FINANCIAL: 'Financeiro',
}

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente',
  ACCEPTED: 'Aceito',
  EXPIRED: 'Expirado',
}

const STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  ACCEPTED: 'success',
  EXPIRED: 'neutral',
}

const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  USER_DEACTIVATED: 'Acesso revogado',
  USER_REACTIVATED: 'Acesso reativado',
  USER_ROLE_CHANGED: 'Perfil alterado',
  USER_PASSWORD_RESET_ISSUED: 'Reset de senha emitido',
  USER_INVITED: 'Usuário convidado',
  USER_INVITE_ACCEPTED: 'Convite aceito',
  ROLE_CREATED: 'Função criada',
  ROLE_UPDATED: 'Função editada',
  ROLE_DELETED: 'Função excluída',
  COMPANY_CREATED: 'Empresa cadastrada',
  COMPANY_UPDATED: 'Empresa editada',
  COMPANY_DEACTIVATED: 'Empresa desativada',
  COMPANY_REACTIVATED: 'Empresa reativada',
  DRIVER_CREATED: 'Motorista cadastrado',
  DRIVER_UPDATED: 'Motorista editado',
  DRIVER_DEACTIVATED: 'Motorista desativado',
  DRIVER_REACTIVATED: 'Motorista reativado',
  VEHICLE_CREATED: 'Veículo cadastrado',
  VEHICLE_UPDATED: 'Veículo editado',
  VEHICLE_DEACTIVATED: 'Veículo desativado',
  VEHICLE_REACTIVATED: 'Veículo reativado',
  FREIGHT_CREATED: 'Frete criado',
  FREIGHT_UPDATED: 'Frete editado',
  FREIGHT_FINANCE_UPDATED: 'Valores do frete alterados',
  FREIGHT_ASSIGNED: 'Frete atribuído',
  FREIGHT_STARTED: 'Frete iniciado',
  FREIGHT_DELIVERED: 'Frete entregue',
  FREIGHT_PROOF_RECEIVED: 'Comprovante recebido',
  FREIGHT_COMPLETED: 'Frete concluído',
  INVOICE_CREATED: 'Fatura criada',
  INVOICE_UPDATED: 'Fatura editada',
  INVOICE_DELETED: 'Fatura excluída',
  INVOICE_PAYMENT_CREATED: 'Pagamento de fatura registrado',
  REPAYMENT_UPDATED: 'Repasse editado',
  REPAYMENT_PAID: 'Repasse pago',
  CASH_ACCOUNT_CREATED: 'Conta de caixa criada',
  CASH_ACCOUNT_UPDATED: 'Conta de caixa editada',
  CASH_ACCOUNT_DEACTIVATED: 'Conta de caixa desativada',
  CASH_ACCOUNT_REACTIVATED: 'Conta de caixa reativada',
  CASH_CATEGORY_CREATED: 'Categoria criada',
  CASH_CATEGORY_UPDATED: 'Categoria editada',
  CASH_CATEGORY_DEACTIVATED: 'Categoria desativada',
  CASH_CATEGORY_REACTIVATED: 'Categoria reativada',
  CASH_TRANSACTION_CREATED: 'Lançamento criado',
  CASH_TRANSACTION_TRANSFER: 'Transferência entre contas',
  CASH_TRANSACTION_REVERSED: 'Lançamento estornado',
  EMPLOYEE_CREATED: 'Funcionário cadastrado',
  EMPLOYEE_UPDATED: 'Funcionário editado',
  EMPLOYEE_TERMINATED: 'Funcionário desligado',
  EMPLOYEE_REACTIVATED: 'Funcionário reativado',
  SALARY_PAYMENT_CREATED: 'Salário pago',
  OPERATIONAL_EXPENSE_CREATED: 'Despesa registrada',
  FINANCIAL_OBLIGATION_CREATED: 'Conta a pagar/receber criada',
  FINANCIAL_OBLIGATION_UPDATED: 'Conta a pagar/receber editada',
  FINANCIAL_OBLIGATION_PAID: 'Conta quitada',
  FINANCIAL_OBLIGATION_CANCELED: 'Conta cancelada',
}

function humanizeAction(action: string) {
  return action.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function extractApiMessage(err: unknown, fallback: string) {
  if (isAxiosError(err)) {
    return err.response?.data?.error?.message ?? fallback
  }
  return fallback
}

// ─── Modais ───────────────────────────────────────────────────────────────────

function ModalShell({
  title,
  onClose,
  children,
}: {
  readonly title: string
  readonly onClose: () => void
  readonly children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function InviteModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void
  readonly onCreated: (invite: CreatedInvite) => void
}) {
  const { data: roles, isLoading: rolesLoading } = useRoles()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateInviteFormData>({ resolver: zodResolver(createInviteSchema) })

  async function onSubmit(data: CreateInviteFormData) {
    try {
      const res = await api.post<CreatedInvite>('/invites', data)
      onCreated(res.data)
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao criar convite'))
    }
  }

  return (
    <ModalShell title="Convidar usuário" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            placeholder="colaborador@empresa.com"
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-roleId">Função</Label>
          <select
            id="invite-roleId"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            disabled={rolesLoading}
            {...register('roleId')}
          >
            <option value="">{rolesLoading ? 'Carregando…' : 'Selecione uma função'}</option>
            {(roles ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {errors.roleId && <p className="text-xs text-destructive">{errors.roleId.message}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Enviando…' : 'Gerar convite'}
          </Button>
        </div>
      </form>
    </ModalShell>
  )
}

function InviteLinkCard({ invite, onClose }: { readonly invite: CreatedInvite; readonly onClose: () => void }) {
  const link = `${window.location.origin}/accept-invite/${invite.token}`
  return (
    <ModalShell title="Convite gerado" onClose={onClose}>
      <p className="mb-3 text-sm text-muted-foreground">
        Compartilhe com <strong>{invite.email}</strong> ({invite.tenantRole?.name ?? ROLE_LABEL[invite.role]}). Válido por 7 dias.
      </p>
      <CopyableLink url={link} hint="Envie via WhatsApp para o usuário acessar o cadastro." />
      <Button className="mt-4 w-full" onClick={onClose}>Fechar</Button>
    </ModalShell>
  )
}

function ResetLinkCard({ user, url, onClose }: { readonly user: TeamMember; readonly url: string; readonly onClose: () => void }) {
  return (
    <ModalShell title="Link de reset gerado" onClose={onClose}>
      <p className="mb-3 text-sm text-muted-foreground">
        Compartilhe com <strong>{user.email}</strong>. Válido por 24 horas. As sessões ativas dele serão invalidadas após o reset.
      </p>
      <CopyableLink url={url} hint="Envie via WhatsApp para o usuário criar uma nova senha." />
      <Button className="mt-4 w-full" onClick={onClose}>Fechar</Button>
    </ModalShell>
  )
}

function ConfirmModal({
  title,
  message,
  confirmLabel,
  destructive,
  onConfirm,
  onClose,
  loading,
}: {
  readonly title: string
  readonly message: React.ReactNode
  readonly confirmLabel: string
  readonly destructive?: boolean
  readonly onConfirm: () => void
  readonly onClose: () => void
  readonly loading?: boolean
}) {
  return (
    <ModalShell title={title} onClose={onClose}>
      <div className="text-sm text-muted-foreground">{message}</div>
      <div className="mt-5 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          type="button"
          variant={destructive ? 'destructive' : 'default'}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Processando…' : confirmLabel}
        </Button>
      </div>
    </ModalShell>
  )
}

function RoleModal({
  user,
  onClose,
  onConfirm,
  loading,
}: {
  readonly user: TeamMember
  readonly onClose: () => void
  readonly onConfirm: (roleId: string) => void
  readonly loading?: boolean
}) {
  const { data: roles, isLoading: rolesLoading } = useRoles()
  const [roleId, setRoleId] = useState<string>(user.tenantRoleId ?? '')

  return (
    <ModalShell title="Alterar função" onClose={onClose}>
      <p className="mb-3 text-sm text-muted-foreground">
        Alterando função de <strong>{user.name}</strong> ({user.email}).
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="role-select">Função</Label>
        <select
          id="role-select"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          disabled={rolesLoading}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">{rolesLoading ? 'Carregando…' : 'Selecione uma função'}</option>
          {(roles ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          type="button"
          onClick={() => onConfirm(roleId)}
          disabled={loading || !roleId || roleId === user.tenantRoleId}
        >
          {loading ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </ModalShell>
  )
}

// ─── Tab: Perfil ──────────────────────────────────────────────────────────────

function ProfileTab({ user }: { readonly user: UserMe }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: user.name, currentPassword: '', newPassword: '' },
  })

  async function onSubmit(data: ProfileFormData) {
    const payload: Record<string, string> = {}
    if (data.name && data.name !== user.name) payload.name = data.name
    if (data.newPassword) {
      payload.currentPassword = data.currentPassword ?? ''
      payload.newPassword = data.newPassword
    }
    if (Object.keys(payload).length === 0) return

    try {
      await api.put('/users/me', payload)
      toast.success('Perfil atualizado')
      reset({ name: data.name ?? user.name, currentPassword: '', newPassword: '' })
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao atualizar perfil'))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="prof-name">Nome</Label>
        <Input id="prof-name" placeholder="Seu nome" aria-invalid={!!errors.name} {...register('name')} />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input value={user.email} disabled className="bg-muted" />
        <p className="text-xs text-muted-foreground">O email não pode ser alterado.</p>
      </div>
      <div className="border-t border-border pt-4">
        <p className="mb-3 text-sm font-medium text-foreground">Alterar senha</p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cur-pass">Senha atual</Label>
            <Input id="cur-pass" type="password" placeholder="••••••••" aria-invalid={!!errors.currentPassword} {...register('currentPassword')} />
            {errors.currentPassword && <p className="text-xs text-destructive">{errors.currentPassword.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pass">Nova senha</Label>
            <Input id="new-pass" type="password" placeholder="Mínimo 6 caracteres" aria-invalid={!!errors.newPassword} {...register('newPassword')} />
            {errors.newPassword && <p className="text-xs text-destructive">{errors.newPassword.message}</p>}
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting || !isDirty}>
          {isSubmitting ? 'Salvando…' : 'Salvar alterações'}
        </Button>
      </div>
    </form>
  )
}

// ─── Tab: Equipe ──────────────────────────────────────────────────────────────

type TeamAction =
  | { type: 'role'; user: TeamMember }
  | { type: 'deactivate'; user: TeamMember }
  | { type: 'reactivate'; user: TeamMember }
  | { type: 'reset'; user: TeamMember }

function TeamTab({ currentUserId }: { readonly currentUserId: string }) {
  const { has } = usePermissions()
  const canInvite = has('team.invite')
  const canManageUsers = has('team.manage_users')
  const queryClient = useQueryClient()
  const [showInvite, setShowInvite] = useState(false)
  const [createdInvite, setCreatedInvite] = useState<CreatedInvite | null>(null)
  const [action, setAction] = useState<TeamAction | null>(null)
  const [resetResult, setResetResult] = useState<{ user: TeamMember; url: string } | null>(null)

  const team = useTeam()
  const updateRole = useUpdateUserRole()
  const deactivate = useDeactivateUser()
  const reactivate = useReactivateUser()
  const issueReset = useGeneratePasswordReset()

  const { data: invites = [], isLoading: loadingInvites } = useQuery<Invite[]>({
    queryKey: ['invites'],
    queryFn: () => api.get<Invite[]>('/invites').then((r) => r.data),
    enabled: canInvite,
  })

  function handleInviteCreated(invite: CreatedInvite) {
    setShowInvite(false)
    setCreatedInvite(invite)
    queryClient.invalidateQueries({ queryKey: ['invites'] })
    toast.success(`Convite gerado para ${invite.email}`)
  }

  async function handleConfirm() {
    if (!action) return
    try {
      if (action.type === 'role') {
        // handled by RoleModal directly
        return
      }
      if (action.type === 'deactivate') {
        await deactivate.mutateAsync(action.user.id)
        toast.success(`Acesso de ${action.user.name} revogado`)
      } else if (action.type === 'reactivate') {
        await reactivate.mutateAsync(action.user.id)
        toast.success(`Acesso de ${action.user.name} reativado`)
      } else if (action.type === 'reset') {
        const result = await issueReset.mutateAsync(action.user.id)
        setResetResult({ user: action.user, url: result.resetUrl })
      }
      setAction(null)
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao executar ação'))
    }
  }

  async function handleRoleSave(roleId: string) {
    if (!action || action.type !== 'role') return
    try {
      const updated = await updateRole.mutateAsync({ id: action.user.id, roleId })
      toast.success(
        `Função de ${action.user.name} alterada para ${updated.tenantRole?.name ?? ROLE_LABEL[updated.role]}`,
      )
      setAction(null)
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao alterar função'))
    }
  }

  const members = team.data?.data ?? []
  const pendingInvites = invites.filter((i) => i.status === 'PENDING')

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Membros da equipe.</p>
          {canInvite && (
            <Button size="sm" onClick={() => setShowInvite(true)}>
              <UserPlus size={14} />
              Convidar
            </Button>
          )}
        </div>

        {team.isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Carregando…</p>
        ) : members.length === 0 ? (
          <div className="mt-4 rounded-lg border border-border bg-background p-8 text-center">
            <p className="text-sm text-muted-foreground">Nenhum usuário cadastrado.</p>
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Usuário</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Perfil</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((m) => {
                  const isSelf = m.id === currentUserId
                  return (
                    <tr key={m.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{m.name}{isSelf && <span className="ml-1 text-xs text-muted-foreground">(você)</span>}</p>
                        <p className="text-xs text-muted-foreground">{m.email}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{m.tenantRole?.name ?? ROLE_LABEL[m.role]}</td>
                      <td className="px-4 py-3">
                        <Badge tone={m.active ? 'success' : 'neutral'}>
                          {m.active ? 'Ativo' : 'Revogado'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {canManageUsers && !isSelf && (
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => setAction({ type: 'role', user: m })}>
                              Perfil
                            </Button>
                            {m.active ? (
                              <>
                                <Button size="sm" variant="outline" onClick={() => setAction({ type: 'reset', user: m })}>
                                  Resetar senha
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => setAction({ type: 'deactivate', user: m })}>
                                  Revogar
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => setAction({ type: 'reactivate', user: m })}>
                                Reativar
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canInvite && (
        <div>
          <p className="text-sm font-medium text-foreground">Convites</p>
          <p className="text-xs text-muted-foreground">Histórico de convites enviados.</p>

          {loadingInvites ? (
            <p className="mt-4 text-sm text-muted-foreground">Carregando…</p>
          ) : invites.length === 0 ? (
            <div className="mt-3 rounded-lg border border-border bg-background p-6 text-center">
              <p className="text-sm text-muted-foreground">Nenhum convite enviado ainda.</p>
            </div>
          ) : (
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Perfil</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Expira em</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invites.map((inv) => (
                    <tr key={inv.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-foreground">{inv.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">{inv.tenantRole?.name ?? ROLE_LABEL[inv.role]}</td>
                      <td className="px-4 py-3">
                        <Badge tone={STATUS_TONE[inv.status] ?? 'neutral'}>
                          {STATUS_LABEL[inv.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(inv.expiresAt).toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pendingInvites.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {pendingInvites.length} convite(s) aguardando aceite.
            </p>
          )}
        </div>
      )}

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onCreated={handleInviteCreated} />}
      {createdInvite && <InviteLinkCard invite={createdInvite} onClose={() => setCreatedInvite(null)} />}

      {action?.type === 'role' && (
        <RoleModal
          user={action.user}
          onClose={() => setAction(null)}
          onConfirm={handleRoleSave}
          loading={updateRole.isPending}
        />
      )}
      {action?.type === 'deactivate' && (
        <ConfirmModal
          title="Revogar acesso"
          message={
            <span>
              Tem certeza que deseja <strong>revogar o acesso</strong> de <strong>{action.user.name}</strong>?
              <br />
              O usuário deixa de conseguir entrar até ser reativado.
            </span>
          }
          confirmLabel="Revogar"
          destructive
          onConfirm={handleConfirm}
          onClose={() => setAction(null)}
          loading={deactivate.isPending}
        />
      )}
      {action?.type === 'reactivate' && (
        <ConfirmModal
          title="Reativar acesso"
          message={
            <span>
              Reativar o acesso de <strong>{action.user.name}</strong>?
            </span>
          }
          confirmLabel="Reativar"
          onConfirm={handleConfirm}
          onClose={() => setAction(null)}
          loading={reactivate.isPending}
        />
      )}
      {action?.type === 'reset' && (
        <ConfirmModal
          title="Gerar link de reset"
          message={
            <span>
              Gerar um link de reset de senha para <strong>{action.user.name}</strong>?
              <br />
              Você poderá copiar e enviar via WhatsApp.
            </span>
          }
          confirmLabel="Gerar link"
          onConfirm={handleConfirm}
          onClose={() => setAction(null)}
          loading={issueReset.isPending}
        />
      )}
      {resetResult && (
        <ResetLinkCard
          user={resetResult.user}
          url={resetResult.url}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  )
}

// ─── Tab: Atividade ───────────────────────────────────────────────────────────

function ActivityTab() {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useAuditLogs({ page, perPage: 20 })

  const rows = data?.data ?? []

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-foreground">Histórico de atividades</p>
        <p className="text-xs text-muted-foreground">
          Ações administrativas executadas na sua transportadora (incluindo as feitas pelo suporte NVS).
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-background p-8 text-center">
          <p className="text-sm text-muted-foreground">Nenhuma atividade registrada ainda.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Quando</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Ação</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Alvo</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Por</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((log) => {
                const isStaff = !!log.actorStaffUserId
                const meta = log.metadata
                let detail = ''
                if (log.action === 'USER_ROLE_CHANGED' && meta) {
                  detail = ` (${ROLE_LABEL[String(meta['oldRole'])] ?? meta['oldRole']} → ${ROLE_LABEL[String(meta['newRole'])] ?? meta['newRole']})`
                } else if (log.action === 'USER_INVITED' && meta) {
                  detail = ` (${ROLE_LABEL[String(meta['role'])] ?? meta['role']})`
                } else if (log.action === 'ROLE_UPDATED' && meta) {
                  const oldP = Array.isArray(meta['oldPermissions']) ? (meta['oldPermissions'] as string[]) : []
                  const newP = Array.isArray(meta['newPermissions']) ? (meta['newPermissions'] as string[]) : []
                  const added = newP.filter((p) => !oldP.includes(p)).length
                  const removed = oldP.filter((p) => !newP.includes(p)).length
                  if (added || removed) detail = ` (+${added} / -${removed} permissões)`
                } else if (log.action === 'FREIGHT_FINANCE_UPDATED' && meta) {
                  const f = meta['freightValue']
                  const d = meta['driverValue']
                  const parts: string[] = []
                  if (f != null) parts.push(`valor ${f}`)
                  if (d != null) parts.push(`motorista ${d}`)
                  if (parts.length) detail = ` (${parts.join(', ')})`
                } else if (log.action === 'CASH_TRANSACTION_TRANSFER' && meta) {
                  const from = meta['fromAccount']
                  const to = meta['toAccount']
                  if (from && to) detail = ` (${from} → ${to})`
                }
                const label = AUDIT_ACTION_LABEL[log.action] ?? humanizeAction(log.action)
                return (
                  <tr key={log.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{formatDateTime(log.createdAt)}</td>
                    <td className="px-4 py-3 text-foreground">
                      {label}{detail}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{log.targetLabel ?? log.targetId}</td>
                    <td className="px-4 py-3">
                      <span className="text-foreground">{log.actorName}</span>
                      {isStaff && (
                        <span className="ml-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                          suporte NVS
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Página {data.page} de {data.totalPages}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tab: Aparência ───────────────────────────────────────────────────────────

function AppearanceTab({ user }: { readonly user: UserMe }) {
  const [color, setColor] = useState(user.tenant.primaryColor)
  const [saving, setSaving] = useState(false)
  const { has } = usePermissions()
  const isAdmin = has('settings.appearance')

  function handleColorChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newColor = e.target.value
    setColor(newColor)
    applyPrimaryColor(newColor)
  }

  async function handleSave() {
    setSaving(true)
    try {
      await api.put('/users/tenant/config', { primaryColor: color })
      setPrimaryColor(color)
      toast.success('Cor salva com sucesso')
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao salvar cor'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium text-foreground">Cor primária</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Define a cor dos botões e elementos de destaque para todos os usuários da transportadora.
        </p>
      </div>

      <div className="flex items-center gap-4">
        <input
          type="color"
          value={color}
          onChange={handleColorChange}
          disabled={!isAdmin}
          className="h-10 w-16 cursor-pointer rounded border border-border bg-background p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <div>
          <p className="text-sm font-medium text-foreground">{color}</p>
          <p className="text-xs text-muted-foreground">Pré-visualização em tempo real</p>
        </div>
      </div>

      {!isAdmin && (
        <p className="text-xs text-muted-foreground">Somente administradores podem alterar a cor.</p>
      )}

      {isAdmin && (
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar cor'}
        </Button>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Tab = 'profile' | 'team' | 'roles' | 'activity' | 'appearance'

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('profile')
  const { has } = usePermissions()

  const { data: user, isLoading } = useQuery<UserMe>({
    queryKey: ['users', 'me'],
    queryFn: () => api.get<UserMe>('/users/me').then((r) => r.data),
  })

  if (isLoading || !user) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  const canViewAudit = has('settings.view_audit')
  const canManageRoles = has('team.manage_roles')
  const tabs: { id: Tab; label: string; visible: boolean }[] = [
    { id: 'profile', label: 'Perfil', visible: true },
    { id: 'team', label: 'Equipe', visible: true },
    { id: 'roles', label: 'Funções', visible: canManageRoles },
    { id: 'activity', label: 'Atividade', visible: canViewAudit },
    { id: 'appearance', label: 'Aparência', visible: true },
  ]
  const visibleTabs = tabs.filter((t) => t.visible)

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <h1 className="text-xl font-semibold text-foreground">Configurações</h1>

      <div className="flex border-b border-border">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'profile' && <ProfileTab user={user} />}
        {tab === 'team' && <TeamTab currentUserId={user.id} />}
        {tab === 'roles' && canManageRoles && <RolesTab />}
        {tab === 'activity' && canViewAudit && <ActivityTab />}
        {tab === 'appearance' && <AppearanceTab user={user} />}
      </div>
    </div>
  )
}
